import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import BottomSheet from '../../DesignSystem/BottomSheet';
import s from './TimeSelection.module.scss';
import { ReactComponent as EveningIcon } from '../../Icons/Evening.svg';
import { ReactComponent as AfternoonIcon } from '../../Icons/Afternoon.svg';
import { ReactComponent as MorningIcon } from '../../Icons/Morning.svg';
import {
  getLabSlots,
  getRescheduleLabOrderReasons,
  rescheduleLabOrder,
  selectSlot,
} from '../../../../../services';
import { ReactComponent as SpinnerIcon } from '../../Icons/SpinnerIcon.svg';
import { ReactComponent as AngleDownIcon } from '../../Icons/AngleDown.svg';
import { logEvent } from '../../../../../utils/logEvent';
import DefaultLabImage from '../../Images/DefaultLab.png';
import {
  formatDateWithOrdinal,
  formatSlotLabel,
  getTabForCurrentTime,
  getFirstTabWithEnabledSlots,
  getDaysDiffFromToday,
  isDateEnabled,
  clsx,
} from '../../utils';
import LoadingComponent from '../../../../../components/common/LoadingComponent';

/* ================= Component ================= */

const TimeSelection = ({
  isOpen,
  onClose,
  onConfirm,
  cartId,
  type,
  selectedPartner,
  isScanAndPay,
  hospitalLogo,
  isReschedule,
  orderId,
  showRescheduleReasons,
}) => {
  const authToken = useSelector((state) => state.user.authToken);
  const [selectedReason, setSelectedReason] = useState(null);
  const [showReasonOptions, setShowReasonOptions] = useState(false);

  const [slotsData, setSlotsData] = useState([]);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [activeTab, setActiveTab] = useState('Morning');
  const [loading, setLoading] = useState(true);
  const [tatvaSuggestionId, setTatvaSuggestionId] = useState(null);
  const [reasonsLoading, setReasonsLoading] = useState(true);
  const [rescheduleReasons, setRescheduleReasons] = useState([]);
  /* ================= Fetch Slots ================= */

  const fetchSlots = useCallback(() => {
    if (isReschedule ? !orderId : !isScanAndPay && !cartId) return;

    setLoading(true);
    const request = isReschedule
      ? getLabSlots(null, authToken, false, orderId)
      : getLabSlots(cartId, authToken, isScanAndPay);

    request
      .then((res) => {
        const slots = res?.slots || [];
        setSlotsData(slots);

        const firstEnabledIndex = slots.findIndex(isDateEnabled);

        if (firstEnabledIndex !== -1) {
          const selectedDate = slots[firstEnabledIndex];

          setSelectedDayIndex(firstEnabledIndex);
          setSelectedSlotId(null);
          setActiveTab(
            getFirstTabWithEnabledSlots(
              selectedDate?.time || [],
              selectedDate?.date
            )
          );

          if (isScanAndPay) {
            logEvent('Scan QR Date Selected', {
              day: selectedDate.day,
              daysFromCurrentDate: getDaysDiffFromToday(selectedDate.date),
              cartId,
              visitType: type,
              autoSelected: true,
            });
          } else {
            logEvent('Date Selected', {
              day: selectedDate.day,
              daysFromCurrentDate: getDaysDiffFromToday(selectedDate.date),
              cartId,
              visitType: type,
              autoSelected: true,
            });
          }
        }
      })
      .catch((err) => {
        console.error('Error fetching slots:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isScanAndPay, cartId, authToken, type, isReschedule, orderId]);

  useEffect(() => {
    if (isOpen) {
      setSelectedSlotId(null);
      setSelectedDayIndex(0);
      fetchSlots();
    }
  }, [isOpen, fetchSlots]);

  /* ================= Derived Data ================= */

  const currentDaySlots = useMemo(
    () => slotsData[selectedDayIndex]?.time || [],
    [slotsData, selectedDayIndex]
  );

  const categorizedSlots = useMemo(() => {
    const morning = [];
    const afternoon = [];
    const evening = [];

    currentDaySlots.forEach((slot) => {
      const label = slot.label.toLowerCase();
      const hour = parseInt(label.split(':')[0], 10);

      if (label.includes('am')) {
        morning.push(slot);
        return;
      }

      if (hour === 12 || hour < 5) {
        afternoon.push(slot);
      } else {
        evening.push(slot);
      }
    });

    return {
      Morning: morning,
      Afternoon: afternoon,
      Evening: evening,
    };
  }, [currentDaySlots]);

  const activeSlots = categorizedSlots[activeTab] || [];

  const hasAnyAvailableSlot = useMemo(
    () =>
      slotsData.some(
        (day) =>
          Array.isArray(day?.time) && day.time.some((slot) => slot.enabled)
      ),
    [slotsData]
  );

  const currentDayHasSlot = useMemo(
    () => currentDaySlots.some((slot) => slot.enabled),
    [currentDaySlots]
  );

  /* ================= Actions ================= */

  const handleConfirm = () => {
    if (!selectedSlotId) return;
    const selectedDay = slotsData[selectedDayIndex];
    const selectedDate = selectedDay?.date;
    if (!selectedDate) return;

    if (isScanAndPay) {
      const selectedSlot = (selectedDay?.time || []).find(
        (sl) => sl.slotId === selectedSlotId
      );
      onConfirm?.({
        slotId: selectedSlotId,
        collectionDate: selectedDate,
        slotDisplayText: `${formatDateWithOrdinal(selectedDate)}, ${selectedSlot?.label}`,
      });
      onClose?.();
      return;
    }

    setLoading(true);
    const request = isReschedule
      ? rescheduleLabOrder(authToken, orderId, selectedSlotId, selectedDate)
      : selectSlot(
          selectedSlotId,
          cartId,
          selectedDate,
          authToken,
          false,
          false,
          tatvaSuggestionId
        );
    request
      .then((res) => {
        onConfirm?.(res);
        logEvent('Slot Submitted', { cartId, visitType: type });
        onClose?.();
      })
      .catch((err) => {
        console.error('Error selecting slot:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClose = () => {
    onClose?.();
    if (!isScanAndPay) {
      logEvent('Slot Selection Screen Dismissed', {
        cartId,
        visitType: type,
      });
    }
  };

  const callRescheduleReasonsApi = async () => {
    if (showReasonOptions) {
      setShowReasonOptions(false);
      return;
    }

    setShowReasonOptions(true);
    setReasonsLoading(true);
    try {
      const res = await getRescheduleLabOrderReasons(authToken, orderId);
      setRescheduleReasons(res?.rescheduleReasons || []);
      console.log('Reschedule reasons:', res);
    } finally {
      setReasonsLoading(false);
    }
  };

  /* ================= UI ================= */

  return (
    <BottomSheet isOpen={isOpen} onClose={handleClose} noPadding>
      <div className={s.container}>
        {/* Header */}
        <div className={s.header}>
          <div className={s.partnerImage}>
            <img
              src={
                isScanAndPay
                  ? hospitalLogo
                  : (selectedPartner?.imageUrl ?? DefaultLabImage)
              }
            />
          </div>
          <p className={s.title}>
            {isScanAndPay ? 'Select Appointment Time' : `Select ${type} Slots`}
          </p>
          <button className={s.closeBtn} onClick={handleClose}>
            ✕
          </button>
        </div>

        {loading && slotsData.length === 0 ? (
          <div className={s.loadingContainer}>
            <div className={s.loader}></div>
          </div>
        ) : !hasAnyAvailableSlot ? (
          <div className={s.noSlotsContainer}>
            <p className={s.noSlots}>No slots available.</p>
          </div>
        ) : (
          <>
            {/* Dates */}
            <div className={s.days}>
              {slotsData.map((d, index) => {
                const isDisabled = !isDateEnabled(d);
                const isActive = index === selectedDayIndex && !isDisabled;

                return (
                  <div
                    key={d.date}
                    className={[
                      s.day,
                      isActive && s.dayActive,
                      isDisabled && s.dayDisabled,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      if (isDisabled) return;

                      setSelectedDayIndex(index);
                      setSelectedSlotId(null);
                      setActiveTab(
                        getFirstTabWithEnabledSlots(d?.time || [], d?.date)
                      );
                      if (isScanAndPay) {
                        logEvent('Scan QR Date Selected', {
                          day: d.day,
                          daysFromCurrentDate: getDaysDiffFromToday(d.date),
                          cartId,
                          visitType: type,
                        });
                      } else {
                        logEvent('Date Selected', {
                          day: d.day,
                          daysFromCurrentDate: getDaysDiffFromToday(d.date),
                          cartId,
                          visitType: type,
                        });
                      }
                    }}
                  >
                    <span className={s.dayName}>{d.label.split(' ')[0]}</span>
                    <span className={s.dayDate}>{d.day}</span>
                  </div>
                );
              })}
            </div>
            {showRescheduleReasons && (
              <div className={s.reasonContainer}>
                <p className={s.reasonLabel}>Reason for Rescheduling</p>
                <div className={s.reasonDropdown}>
                  <div
                    className={s.reasonTrigger}
                    onClick={callRescheduleReasonsApi}
                  >
                    <span
                      className={
                        selectedReason ? s.reasonValue : s.reasonPlaceholder
                      }
                    >
                      {selectedReason || 'Share a reason to help us improve'}
                    </span>
                    <AngleDownIcon
                      className={clsx(
                        s.reasonChevron,
                        showReasonOptions && s.reasonChevronOpen
                      )}
                    />
                  </div>
                  {showReasonOptions && (
                    <ul className={s.reasonOptions}>
                      {reasonsLoading ? (
                        <LoadingComponent />
                      ) : (
                        rescheduleReasons.map((reason) => (
                          <li
                            key={reason}
                            className={clsx(
                              s.reasonOption,
                              selectedReason === reason && s.reasonOptionActive
                            )}
                            onClick={() => {
                              setSelectedReason(reason?.heading);
                              setShowReasonOptions(false);
                            }}
                          >
                            {reason?.heading}
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </div>
              </div>
            )}
            <div className={s.slotsData}>
              {!currentDayHasSlot ? (
                <div className={s.noSlotsContainer}>
                  <p className={s.noSlots}>No slots available for this date.</p>
                </div>
              ) : (
                <>
                  {/* Tabs */}
                  <p className={s.sectionTitle}>SELECT YOUR PREFERRED SLOT</p>

                  <div className={s.slotType}>
                    {['Morning', 'Afternoon', 'Evening'].map((tab) => (
                      <button
                        key={tab}
                        className={activeTab === tab ? s.slotTypeActive : ''}
                        onClick={() => setActiveTab(tab)}
                      >
                        {tab === 'Morning' && <MorningIcon />}
                        {tab === 'Afternoon' && <AfternoonIcon />}
                        {tab === 'Evening' && <EveningIcon />}
                        {tab}
                      </button>
                    ))}
                  </div>

                  {/* Slots */}
                  <div className={s.slots}>
                    {activeSlots.length > 0 ? (
                      activeSlots.map((slot) => (
                        <button
                          key={slot.slotId}
                          className={
                            selectedSlotId === slot.slotId
                              ? s.slotActive
                              : s.slot
                          }
                          disabled={!slot.enabled}
                          onClick={() => {
                            setSelectedSlotId(slot.slotId);
                            setTatvaSuggestionId(slot.tatvaSuggestionId);
                            if (isScanAndPay) {
                              logEvent('Scan QR Time Selected', {
                                slotTime: slot.label,
                                cartId,
                                visitType: type,
                              });
                            } else {
                              logEvent('Time Selected', {
                                slotId: slot.slotId,
                                cartId,
                                visitType: type,
                                slotTime: slot.label,
                              });
                            }
                          }}
                          style={!slot.enabled ? { opacity: 0.5 } : undefined}
                        >
                          {formatSlotLabel(slot)}
                          {selectedSlotId === slot.slotId && (
                            <div className={s.checkmark}>
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M2 6.5L4.75 9L10 3"
                                  fill="none"
                                  stroke="var(--primary-button, #714fff)"
                                  stroke-width="1.6"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </div>
                          )}
                        </button>
                      ))
                    ) : (
                      <p className={s.noSlots}>
                        No slots available for this moment.
                      </p>
                    )}
                  </div>

                  {/* Confirm */}
                  <button
                    className={selectedSlotId ? s.confirm : s.confirmDisabled}
                    disabled={!selectedSlotId || loading}
                    onClick={handleConfirm}
                  >
                    {loading ? (
                      <span
                        style={{
                          animation: 'spin 0.8s linear infinite',
                          display: 'inline-block',
                          margin: '0 auto',
                        }}
                      >
                        <SpinnerIcon />
                      </span>
                    ) : (
                      'Confirm'
                    )}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </BottomSheet>
  );
};

export default TimeSelection;
